"use client";

import { Box, Button, Card, Chip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "./icons/CheckIcon";
import { useTranslations } from "next-intl";

/**
 * 价格卡片组件的属性接口
 */
interface PricingCardProps {
  title: string; // 计划标题，如 "The Explorer"
  subtitle: string; // 计划副标题，如 "Monthly Plan"
  price: string; // 价格，如 "$29"
  period: string; // 计费周期，如 "/month"
  originalPrice?: string; // 原价，如 "$43.99"（可选，用于显示折扣前价格）
  features: string[]; // 功能特性列表
  label?: string; // 可选标签，如 "Most Popular"
  labelColor?: string; // 标签文字颜色
  labelBgColor?: string; // 标签背景颜色
  isSelected?: boolean; // 是否为当前选中状态
  disabled?: boolean; // 是否禁用（用于计划降级限制）
  buttonText?: string; // 按钮文本（可选，不提供时不显示按钮）
  hasHover?: boolean; // 是否启用悬停效果，默认为true
  onClick?: () => void; // 点击回调函数
}

/**
 * 价格卡片组件
 *
 * 主要功能：
 * 1. 展示订阅计划的详细信息
 * 2. 支持选中状态的视觉反馈
 * 3. 支持禁用状态（防止计划降级）
 * 4. 响应式设计和主题适配
 * 5. 优化的布局结构和交互体验
 *
 * 设计特点：
 * - 卡片式布局，清晰的信息层次
 * - 标题和标签水平排列
 * - Benefits部分居中文本带两侧分割线
 * - 主题适配的颜色和阴影
 * - 禁用状态的视觉蒙层效果
 */
export default function PricingCard({
  title,
  subtitle,
  price,
  originalPrice,
  period,
  features,
  label,
  labelColor = "white",
  labelBgColor = "#ff4444",
  isSelected = false,
  disabled = false,
  buttonText,
  hasHover = true, // 默认启用悬停效果
  onClick,
}: PricingCardProps) {
  const theme = useTheme();
  const t = useTranslations("Membership.comparison");

  return (
    <Box sx={{ flex: 1 }}>
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          height: "100%",
          cursor: disabled ? "not-allowed" : "pointer", // 禁用时显示不可点击光标
          border: isSelected ? "2px solid" : "1px solid", // 选中状态加粗边框
          m: isSelected ? "0" : "1px", // 选中时移除外边距以适配加粗边框
          borderColor: isSelected ? "primary.main" : "divider", // 动态边框颜色
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, #232629 0%, #161A1E 100%)"
              : "linear-gradient(180deg, #F2F2F2 0%, #FFFAF5 100%)",
          backdropFilter: "blur(1.25rem)", // 毛玻璃效果
          opacity: disabled ? 0.6 : 1, // 禁用状态透明度
          ...(hasHover && {
            "&:hover": {
              borderColor: disabled ? "divider" : "text.secondary", // 悬停效果
            },
          }),
          borderRadius: "1rem",
          p: "1.5rem", // 内边距使用rem单位
          boxShadow: "none",
        }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* 标题和标签行 - 水平布局 */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography
              variant="h5"
              color="text.primary"
              sx={{
                fontSize: "var(--font-size-s)",
                fontWeight: "600",
                lineHeight: 1.6,
                flex: 1, // 占据剩余空间
              }}>
              {title}
            </Typography>

            {/* 可选标签（如"Most Popular"） */}
            {label && (
              <Chip
                label={label}
                size="small"
                sx={{
                  bgcolor: labelBgColor, // 自定义背景颜色
                  color: labelColor, // 自定义文字颜色
                  fontSize: "var(--font-size-2xs)",
                  lineHeight: 1.6,
                  borderRadius: "0.6875rem",
                  flexShrink: 0, // 不允许收缩
                }}
              />
            )}
          </Box>

          {/* 副标题行 */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "var(--font-size-xs)",
              lineHeight: 1.6,
              mt: "0.25rem",
            }}>
            {subtitle}
          </Typography>

          {/* 价格显示区域 */}
          <Box
            sx={{
              mt: "1.5rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}>
            {/* 当前价格 */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "baseline",
                gap: "0.5rem",
              }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: "var(--font-size-3xl)",
                  lineHeight: 1.3,
                  fontWeight: "600",
                  color: "text.primary",
                }}>
                {"$ " + price}
              </Typography>
              <Typography variant="body2" color="text.tertiary">
                {period}
              </Typography>
            </Box>

            {/* 原价显示（如果有的话） */}
            {originalPrice && originalPrice != price && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "var(--font-size-s)",
                  lineHeight: 1.6,
                  color: "text.tertiary",
                  textDecoration: "line-through",
                  mt: "0.25rem",
                }}>
                {"$ " + originalPrice} {period}
              </Typography>
            )}
          </Box>

          {/* 主要操作按钮 - 仅在提供 buttonText 时显示 */}
          {buttonText && (
            <Button
              fullWidth
              variant="contained"
              color={isSelected ? "primary" : "tertiary"}
              disabled={disabled}
              sx={{
                height: "3.125rem",
                fontWeight: "600",
                mt: "2.5rem",
              }}>
              {buttonText}
            </Button>
          )}

          {/* Benefits with centered text and side dividers */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: buttonText ? "3rem" : "1.5rem", // 动态调整间距
              gap: "0.75rem",
            }}>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "divider",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "var(--font-size-xs)", md: "var(--font-size-s)" }, // 手机端减小字体
                lineHeight: 1.6,
                fontWeight: "600",
                color: "text.primary",
                textAlign: "center",
                flexShrink: 0,
              }}>
              {t("benefits")}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "divider",
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1, mt: "0.75rem", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}>
                <CheckIcon />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "var(--font-size-xs)", lineHeight: 1.6, color: "text.primary" }}>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
